/* ==========================================================================
   GSJ — shipping facts. ONE implementation of two questions:
     "what will checkout charge for shipping?" and
     "how far is this cart from free shipping?"

   Consumed by:
     assets/gs-panels.js         cart drawer + /cart page: bar, summary row, badge
     assets/gsj-pdp-shipfact.js  the product page note (an EComposer code block)
     snippets/gsj-pdp-trust-strip.liquid  via the Liquid mirror below

   snippets/gsj-ship-fact.liquid mirrors this for server-side first paint. Keep
   the two in sync — the theme already uses that pattern for
   snippets/gs-cart-item.liquid <-> GSCart.lineHTML.

   Every threshold, rate and string comes from the "Shipping facts" theme
   settings, emitted once as JSON by layout/theme.liquid. Nothing is written
   here, so the four surfaces can never disagree.
   ========================================================================== */
(function () {
  'use strict';

  var cfg = {};
  try {
    var el = document.getElementById('gsj-ship-config');
    if (el) cfg = JSON.parse(el.textContent || '{}') || {};
  } catch (e) { cfg = {}; }

  function num(v) { var n = parseFloat(v); return isFinite(n) ? n : 0; }
  function toCents(v) { return Math.round(num(v) * 100); }

  /* Compact money formatter driven by shop.money_format. Deliberately standalone
     (not GSCart's makeMoney) so this module has no load-order dependency on
     gs-panels.js — the product page uses it without the cart drawer's JS. */
  function withCommas(cents, decimals, thousands, decimal) {
    var value = (cents / 100).toFixed(decimals == null ? 2 : decimals);
    var parts = value.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousands || ','));
    return dollars + (parts[1] ? (decimal || '.') + parts[1] : '');
  }

  var F = {
    cfg: cfg,

    money: function (cents) {
      cents = Number(cents) || 0;
      var fmt = cfg.moneyFormat || '${{amount}}';
      var ph = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!ph) return '$' + withCommas(cents, 2);
      var val;
      switch (ph[1]) {
        case 'amount_no_decimals': val = withCommas(cents, 0); break;
        case 'amount_with_comma_separator': val = withCommas(cents, 2, '.', ','); break;
        case 'amount_with_space_separator': val = withCommas(cents, 2, ' ', ','); break;
        case 'amount_no_decimals_with_comma_separator': val = withCommas(cents, 0, '.', ','); break;
        case 'amount_no_decimals_with_space_separator': val = withCommas(cents, 0, ' ', ','); break;
        default: val = withCommas(cents, 2);
      }
      return fmt.replace(/\{\{\s*\w+\s*\}\}/, val);
    },

    /* Round amounts read as "$250" rather than "$250.00" in short labels. */
    moneyClean: function (cents) { return this.money(cents).replace(/\.00\b/, ''); },

    /* Which of the delivery profile's three zones applies: Domestic (CA),
       US Cross-border, International. */
    market: function (country) {
      var c = String(country || cfg.country || '').toUpperCase();
      if (c === 'CA') return 'ca';
      if (c === 'US') return 'us';
      return 'intl';
    },

    thresholdCents: function (country) {
      return toCents(this.market(country) === 'ca' ? cfg.freeCA : cfg.freeIntl);
    },

    rateCents: function (country) {
      var m = this.market(country);
      return toCents(m === 'ca' ? cfg.rateCA : m === 'us' ? cfg.rateUS : cfg.rateIntl);
    },

    /* The amount Shopify will actually test its free-shipping rate against.
       Shopify evaluates the rate's TOTAL_PRICE condition AFTER discounts, so
       `after_discounts` (cart.total_price) is the only basis that can never
       over-promise. `before_discounts` is honoured for Canada only: US and
       International advertise exactly the Shopify minimum, so they have no
       cushion for a discount code to eat into. */
    basisCents: function (cart, country) {
      if (!cart) return 0;
      var after = cart.total_price || 0;
      if (cfg.basis !== 'before_discounts') return after;
      if (this.market(country) !== 'ca') return after;
      return cart.original_total_price == null ? after : cart.original_total_price;
    },

    /* What the cart reward ladder would take off an order whose ELIGIBLE
       subtotal is `eligibleCents`. Mirrors the tier walk in
       sections/gs-cart-drawer.liquid; thresholds come from the shop metafield
       gsj.cart_reward_ladder, never from here. Codes are deliberately not
       emitted into the page — only the threshold/reward pairs the estimate
       needs. */
    rewardCents: function (eligibleCents) {
      var rungs = cfg.ladder || [];
      var cap = Number(cfg.ladderCap) || 0;
      var best = 0;
      for (var i = 0; i < rungs.length; i++) {
        if (eligibleCents >= rungs[i].t && rungs[i].r > best) best = rungs[i].r;
      }
      if (cap > 0 && best > cap) best = cap;
      return best;
    },

    /* The whole answer for one cart, in one object. */
    fact: function (basis, country) {
      var threshold = this.thresholdCents(country);
      var rate = this.rateCents(country);
      var unlocked = threshold > 0 && basis >= threshold;
      var remaining = Math.max(0, threshold - basis);
      var rowTpl = unlocked
        ? (cfg.rowFree || 'FREE')
        : (this.market(country) === 'ca' ? (cfg.rowCA || '[rate]') : (cfg.rowOther || '[rate]'));
      var barTpl = unlocked
        ? (cfg.barUnlocked || "YOU'VE UNLOCKED FREE SHIPPING")
        : (cfg.barChase || 'ADD [amount] MORE FOR FREE SHIPPING');
      return {
        threshold: threshold,
        rate: rate,
        basis: basis,
        unlocked: unlocked,
        remaining: remaining,
        /* 0 threshold means no free tier is advertised for this market, so the
           bar hides and the row falls back to the flat rate. */
        active: threshold > 0,
        shipValue: rowTpl.replace('[rate]', this.money(rate)),
        barText: barTpl.replace('[amount]', this.money(remaining)),
        percent: threshold > 0 ? Math.max(0, Math.min(100, (basis / threshold) * 100)) : 0
      };
    },

    /* Product page: the cart plus the piece being viewed, less the reward the
       ladder would auto-apply, so the note agrees with what checkout charges.
       `cartEligible` is the drawer's server-rendered eligible subtotal, which
       is present on every page. */
    pieceFact: function (opts) {
      var piece = Number(opts.pieceCents) || 0;
      var cart = Number(opts.cartCents) || 0;
      var gross = cart + piece;
      var basis = gross;
      if (cfg.basis !== 'before_discounts' || this.market(opts.country) !== 'ca') {
        var eligible = (Number(opts.cartEligibleCents) || 0) + (opts.pieceEligible ? piece : 0);
        basis = gross - this.rewardCents(eligible);
      }
      return this.fact(Math.max(0, basis), opts.country);
    }
  };

  window.GSJShipFact = F;
})();
